/**
 * Estado local de la impersonación del admin de sitio.
 *
 * Mientras el admin navega "como" otro usuario, la sesión de Supabase es la
 * del usuario impersonado; la del admin queda guardada acá para poder volver.
 * Sólo vive en este navegador (localStorage), nunca se sincroniza.
 *
 * Todas las lecturas/escrituras toleran localStorage bloqueado (modo privado,
 * SSR/tests sin window): en ese caso no hay impersonación.
 */

export const IMPERSONATION_STORAGE_KEY = 'mcd:impersonation';

export interface ImpersonationState {
  /** Sesión del admin para restaurarla al salir. */
  adminSession: { access_token: string; refresh_token: string };
  adminUserId: string;
  adminEmail: string | null;
  target: { userId: string; email: string | null; label: string };
  empresa: { id: string; nombre: string };
  /** ISO timestamp de inicio. */
  startedAt: string;
}

function isValidState(value: unknown): value is ImpersonationState {
  const v = value as ImpersonationState | null;
  return (
    !!v &&
    typeof v.adminUserId === 'string' &&
    typeof v.adminSession?.access_token === 'string' &&
    typeof v.adminSession?.refresh_token === 'string' &&
    typeof v.target?.userId === 'string' &&
    typeof v.empresa?.id === 'string'
  );
}

export function getImpersonationState(): ImpersonationState | null {
  try {
    const raw = localStorage.getItem(IMPERSONATION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return isValidState(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function saveImpersonationState(state: ImpersonationState): void {
  localStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(state));
}

export function clearImpersonationState(): void {
  try {
    localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
  } catch {
    /* localStorage bloqueado: nada que limpiar */
  }
}

/**
 * Recarga completa de la app en `path`. Al cambiar de identidad hay que
 * reiniciar todo (caché local, SyncManager, realtime, permisos) con la sesión
 * nueva: un navigate() de React Router no alcanza.
 */
export function recargarAppEn(path: string): void {
  window.location.assign(path);
}

/** Etiqueta legible de un usuario: "Nombre Apellido" o el email. */
export function formatUserLabel(u: {
  nombre?: string | null;
  apellido?: string | null;
  email?: string | null;
}): string {
  const nombre = [u.nombre, u.apellido].map((s) => (s ?? '').trim()).filter(Boolean).join(' ');
  return nombre || u.email || 'Usuario sin nombre';
}
